<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import Policy from '../../policy.vue';
import SectionTitle from 'dashboard/components/ui/SectionTitle.vue';
import { isSidebarItemVisible } from 'dashboard/helper/sidebarNavHelper';

export default {
  components: { SecondaryChildNavItem, Policy, SectionTitle },
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    const { isEnterprise } = useConfig();
    return {
      isAdmin,
      isEnterprise,
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      account: 'getCurrentAccount',
      currentRole: 'getCurrentRole',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isNewFeatureFlagEnabled: 'isNewFeatureFlagEnabled',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      return isSidebarItemVisible(this.menuItem, {
        accountId: this.account.id,
        account: this.account,
        currentRole: this.currentRole,
        globalConfig: this.globalConfig,
        isEnterprise: this.isEnterprise,
        isFeatureEnabledonAccount: this.isFeatureEnabledonAccount,
        isNewFeatureFlagEnabled: this.isNewFeatureFlagEnabled,
      });
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name.includes(this.menuItem.toStateName);
    },

    computedClass() {
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox)
        return 'dark:text-gray-gray-5 dark:hover:text-gray-gray-5';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isCurrentRoute
      ) {
        return 'bg-orange-light-4 dark:bg-orange-dark-4 text-basic-primaryText dark:text-basic-primary hover:bg-orange-light-4 dark:hover:bg-orange-dark-4 hover:text-basic-primaryText dark:hover:text-basic-primary active-view';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'bg-orange-light-4 dark:bg-orange-dark-4 text-basic-primaryText dark:text-basic-primary hover:bg-orange-light-4 dark:hover:bg-orange-dark-4 hover:text-basic-primaryText dark:hover:text-basic-primary active-view';
        }
        return 'dark:text-gray-gray-5 dark:hover:text-gray-gray-5';
      }

      return 'dark:text-gray-gray-5 dark:hover:text-gray-gray-5';
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        e.preventDefault();
        this.$emit(this.menuItem.showModalForNewItem);
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>

<template>
  <li v-show="isMenuItemVisible" class="mt-1 mx-2 px-2">
    <div v-if="menuItem.sectionTitle" class="flex">
      <SectionTitle :label="menuItem.sectionTitle" />
    </div>
    <div v-if="hasSubMenu" class="flex justify-between group">
      <SectionTitle :label="menuItem.label" />
      <div
        v-if="menuItem.showNewButton"
        class="flex items-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="p-0 ml-2"
          @click="$emit('addCustomView')"
        />
      </div>
    </div>
    <router-link
      v-else
      class="flex items-center transition-all p-2 m-0 text-sm font-medium leading-4 rounded-lg text-basic-primaryText hover:bg-gray-gray-2 hover:text-basic-primaryText dark:hover:bg-dark-dark-4"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
    >
      <fluent-icon
        :icon="menuItem.icon"
        :view-box="menuItem.iconType === 'claudia' ? '0 0 16 16' : '0 0 24 24'"
        class="min-w-[1rem] mr-1.5 rtl:mr-0 rtl:ml-1.5"
        :class="{ '-scale-x-100': menuItem.icon === 'rocket' }"
        size="16"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
      <span
        v-if="showChildCount(menuItem.count)"
        class="px-1 py-0 mx-1 font-medium rounded-md text-xxs"
        :class="{
          'text-slate-300 dark:text-slate-600': isCountZero && !isActiveView,
          'text-slate-600 dark:text-slate-50': !isCountZero && !isActiveView,
          'bg-woot-75 dark:bg-woot-200 text-woot-600 dark:text-woot-600':
            isActiveView,
          'bg-slate-50 dark:bg-slate-700': !isActiveView,
        }"
      >
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="inline-block px-1 mx-1 font-medium leading-4 text-green-500 border border-green-400 rounded-lg text-xxs"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>

    <ul v-if="hasSubMenu" class="mb-0 ml-0 list-none">
      <SecondaryChildNavItem
        v-for="child in menuItem.children"
        :key="`${menuItem.key || menuItem.label}-${child.id}`"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="child.visibilityIcon || computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
      />
      <Policy :permissions="['administrator']">
        <router-link
          v-if="menuItem.newLink"
          v-slot="{ href, navigate }"
          :to="menuItem.toState"
          custom
        >
          <li class="pl-1">
            <a :href="href">
              <woot-button
                size="tiny"
                variant="clear"
                color-scheme="secondary"
                icon="add"
                :data-testid="menuItem.dataTestid"
                @click="e => newLinkClick(e, navigate)"
              >
                {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
              </woot-button>
            </a>
          </li>
        </router-link>
      </Policy>
    </ul>
  </li>
</template>
